import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import frCommon from '../locales/fr/common.json';
import frNavigation from '../locales/fr/navigation.json';
import frFooter from '../locales/fr/footer.json';
import frBooking from '../locales/fr/booking.json';
import frAnimal from '../locales/fr/animal.json';
import frForfait from '../locales/fr/forfait.json';
import frDiscovery from '../locales/fr/discovery.json';
import frLegal from '../locales/fr/legal.json';
import frMetiers from '../locales/fr/metiers.json';
import frNative from '../locales/fr/native.json';

import enCommon from '../locales/en/common.json';
import enNavigation from '../locales/en/navigation.json';
import enFooter from '../locales/en/footer.json';
import enBooking from '../locales/en/booking.json';
import enAnimal from '../locales/en/animal.json';
import enForfait from '../locales/en/forfait.json';
import enDiscovery from '../locales/en/discovery.json';
import enLegal from '../locales/en/legal.json';
import enMetiers from '../locales/en/metiers.json';
import enNative from '../locales/en/native.json';

import esCommon from '../locales/es/common.json';
import esNavigation from '../locales/es/navigation.json';
import esFooter from '../locales/es/footer.json';
import esBooking from '../locales/es/booking.json';
import esAnimal from '../locales/es/animal.json';
import esForfait from '../locales/es/forfait.json';
import esDiscovery from '../locales/es/discovery.json';
import esLegal from '../locales/es/legal.json';
import esMetiers from '../locales/es/metiers.json';
import esNative from '../locales/es/native.json';

import deCommon from '../locales/de/common.json';
import deNavigation from '../locales/de/navigation.json';
import deFooter from '../locales/de/footer.json';
import deBooking from '../locales/de/booking.json';
import deAnimal from '../locales/de/animal.json';
import deForfait from '../locales/de/forfait.json';
import deDiscovery from '../locales/de/discovery.json';
import deLegal from '../locales/de/legal.json';
import deMetiers from '../locales/de/metiers.json';
import deNative from '../locales/de/native.json';

import itCommon from '../locales/it/common.json';
import itNavigation from '../locales/it/navigation.json';
import itFooter from '../locales/it/footer.json';
import itBooking from '../locales/it/booking.json';
import itAnimal from '../locales/it/animal.json';
import itForfait from '../locales/it/forfait.json';
import itDiscovery from '../locales/it/discovery.json';
import itLegal from '../locales/it/legal.json';
import itMetiers from '../locales/it/metiers.json';
import itNative from '../locales/it/native.json';

/**
 * Scaffold Flandre / Pays-Bas : namespaces booking + forfait/metiers/native (epic2 overnight).
 * Régions UI : `nl-BE` (🇧🇪) et `nl-NL` (🇳🇱) — ressources partagées `nl`.
 * @see locales/nl/README.md
 * @see config/dutchLocale.ts
 */
import nlCommon from '../locales/nl/common.json';
import nlNavigation from '../locales/nl/navigation.json';
import nlFooter from '../locales/nl/footer.json';
import nlBooking from '../locales/nl/booking.json';
import nlDiscovery from '../locales/nl/discovery.json';
import nlAnimal from '../locales/nl/animal.json';
import nlLegal from '../locales/nl/legal.json';
import nlForfait from '../locales/nl/forfait.json';
import nlMetiers from '../locales/nl/metiers.json';
import nlNative from '../locales/nl/native.json';
import {
  DUTCH_LOCALE_META,
  isDutchRegionalCode,
  normalizeDutchRegionalCode,
  type DutchRegionalCode,
} from './dutchLocale';

export const BOOKING_LANGUAGES = ['fr', 'en', 'es', 'de', 'it', 'nl-BE', 'nl-NL'] as const;
export type BookingLanguageCode = (typeof BOOKING_LANGUAGES)[number];

/** Alias legacy encore rencontré dans URLs / storage. */
export type BookingLanguageCodeOrLegacy = BookingLanguageCode | 'nl';

export const AVAILABLE_LANGUAGES: {
  code: BookingLanguageCode;
  name: string;
  flag: string;
}[] = [
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'nl-BE', name: DUTCH_LOCALE_META['nl-BE'].name, flag: DUTCH_LOCALE_META['nl-BE'].flag },
  { code: 'nl-NL', name: DUTCH_LOCALE_META['nl-NL'].name, flag: DUTCH_LOCALE_META['nl-NL'].flag },
];

export function normalizeBookingLanguage(lang: string | null | undefined): BookingLanguageCode {
  if (!lang) return 'fr';
  const trimmed = lang.trim().replace('_', '-');
  if (BOOKING_LANGUAGES.includes(trimmed as BookingLanguageCode)) {
    return trimmed as BookingLanguageCode;
  }
  const dutch = normalizeDutchRegionalCode(trimmed);
  if (dutch) return dutch;
  const code = trimmed.split('-')[0].toLowerCase();
  if (code === 'nl') return 'nl-BE';
  return BOOKING_LANGUAGES.includes(code as BookingLanguageCode)
    ? (code as BookingLanguageCode)
    : 'fr';
}

/** Langue dayjs / dossiers i18n partagés (nl-BE|nl-NL → nl). */
export function bookingResourceLanguage(lang: string | null | undefined): string {
  const normalized = normalizeBookingLanguage(lang);
  if (isDutchRegionalCode(normalized)) return 'nl';
  return normalized;
}

export function isDutchBookingLanguage(
  lang: string | null | undefined
): lang is DutchRegionalCode {
  return isDutchRegionalCode(normalizeBookingLanguage(lang));
}

export const PREFERRED_LANGUAGE_STORAGE_KEY = 'preferredLanguage';

/** Langue dérivée de l’URL (pas du navigateur) — voir ticket #1093. */
export function detectBookingLanguageFromWindow(): BookingLanguageCode {
  if (typeof window === 'undefined') return 'fr';
  // require différé : localePathPrefix sans i18n (évite cycle localeRoutes ↔ i18n).
  const { getLocaleFromPath } = require('./localePathPrefix') as typeof import('./localePathPrefix');
  return normalizeBookingLanguage(getLocaleFromPath(window.location.pathname) ?? 'fr');
}

const bookingPathLanguageDetector = {
  type: 'languageDetector' as const,
  async: false as const,
  init() {},
  detect(): string | undefined {
    return detectBookingLanguageFromWindow();
  },
  cacheUserLanguage(lng: string) {
    try {
      localStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, normalizeBookingLanguage(lng));
    } catch {
      /* ignore */
    }
  },
};

const nlResources = {
  common: nlCommon,
  navigation: nlNavigation,
  footer: nlFooter,
  booking: nlBooking,
  discovery: nlDiscovery,
  animal: nlAnimal,
  legal: nlLegal,
  forfait: nlForfait,
  metiers: nlMetiers,
  native: nlNative,
};

i18n
  .use(bookingPathLanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: {
        common: frCommon,
        navigation: frNavigation,
        footer: frFooter,
        booking: frBooking,
        animal: frAnimal,
        forfait: frForfait,
        discovery: frDiscovery,
        legal: frLegal,
        metiers: frMetiers,
        native: frNative,
      },
      en: {
        common: enCommon,
        navigation: enNavigation,
        footer: enFooter,
        booking: enBooking,
        animal: enAnimal,
        forfait: enForfait,
        discovery: enDiscovery,
        legal: enLegal,
        metiers: enMetiers,
        native: enNative,
      },
      es: {
        common: esCommon,
        navigation: esNavigation,
        footer: esFooter,
        booking: esBooking,
        animal: esAnimal,
        forfait: esForfait,
        discovery: esDiscovery,
        legal: esLegal,
        metiers: esMetiers,
        native: esNative,
      },
      de: {
        common: deCommon,
        navigation: deNavigation,
        footer: deFooter,
        booking: deBooking,
        animal: deAnimal,
        forfait: deForfait,
        discovery: deDiscovery,
        legal: deLegal,
        metiers: deMetiers,
        native: deNative,
      },
      it: {
        common: itCommon,
        navigation: itNavigation,
        footer: itFooter,
        booking: itBooking,
        animal: itAnimal,
        forfait: itForfait,
        discovery: itDiscovery,
        legal: itLegal,
        metiers: itMetiers,
        native: itNative,
      },
      nl: nlResources,
      'nl-BE': nlResources,
      'nl-NL': nlResources,
    },
    fallbackLng: 'fr',
    supportedLngs: ['fr', 'en', 'es', 'de', 'it', 'nl-BE', 'nl-NL', 'nl'],
    nonExplicitSupportedLngs: true,
    load: 'currentOnly',
    defaultNS: 'common',
    ns: ['common', 'navigation', 'footer', 'booking', 'animal', 'forfait', 'discovery', 'legal', 'metiers', 'native'],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    detection: {
      order: ['querystring', 'languageDetector'],
      lookupQuerystring: 'lang',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng: string) => normalizeBookingLanguage(lng),
    },
    debug: process.env.NODE_ENV === 'development',
  });

export default i18n;
